/*
 * Paint Pals AI chat widget ("Alice") for the Prosperity Painting Consultants storefront.
 * Vanilla JS, no dependencies. Renders inside a shadow root so the Dawn theme CSS
 * can't bleed into it (and vice-versa). Config comes from window.PaintPalsChatConfig,
 * set in snippets/paintpals-chat.liquid.
 *
 * Talks to the custom Convex backend (site/convex): POST {endpoint} with
 * { message, threadId? } -> { reply, threadId, model }.
 *
 * Uninstall: remove the {% render 'paintpals-chat' %} include from layout/theme.liquid
 * and delete this asset + the snippet.
 */
(function () {
  "use strict";

  var cfg = window.PaintPalsChatConfig || {};
  var ENDPOINT = (cfg.endpoint || "").trim();
  // Stay hidden until a real endpoint is configured, so an early theme push never
  // shows a broken widget.
  if (!ENDPOINT || ENDPOINT.indexOf("REPLACE_WITH") !== -1) return;
  if (window.__paintPalsChatLoaded) return;
  window.__paintPalsChatLoaded = true;

  var TITLE = cfg.title || "Paint Pals Helper";
  var GREETING =
    cfg.greeting ||
    "Hey, I'm Alice 👋 — here to help painters estimate smarter and grow. Ask me anything!";
  var ACCENT = cfg.accent || "#EA1212";
  var INK = "#060202";
  var PAPER = "#fdfbf7";
  var THREAD_KEY = "pp_chat_thread";

  var threadId = null;
  try { threadId = localStorage.getItem(THREAD_KEY) || null; } catch (e) {}

  function esc(s) {
    var d = document.createElement("div");
    d.textContent = s == null ? "" : String(s);
    return d.innerHTML;
  }

  // Bot text: escape everything, then render markdown links, **bold**, and bare URLs.
  function renderBot(text) {
    var safe = esc(text);
    safe = safe.replace(/\[([^\]]+)\]\((https?:\/\/[^\s)]+)\)/g, function (_, label, url) {
      return '<a href="' + url + '" target="_blank" rel="noopener">' + label + "</a>";
    });
    safe = safe.replace(/(^|[^">=])(https?:\/\/[^\s<]+)/g, function (_, pre, url) {
      return pre + '<a href="' + url + '" target="_blank" rel="noopener">' + url + "</a>";
    });
    safe = safe.replace(/\*\*([^*]+)\*\*/g, "<strong>$1</strong>");
    return safe;
  }

  // ---- shadow host ----
  var host = document.createElement("div");
  host.id = "paintpals-chat";
  // Some Shopify themes hide bare/unclassed elements (and :host{all:initial}
  // collapses the div), which would hide the whole widget. Force the host
  // visible with an inline !important rule so its fixed-position launcher/panel
  // always render, regardless of theme CSS.
  host.style.setProperty("display", "block", "important");
  document.body.appendChild(host);
  var root = host.attachShadow({ mode: "open" });

  var style = document.createElement("style");
  style.textContent = [
    ":host{all:initial;}",
    "*{box-sizing:border-box;font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,Helvetica,Arial,sans-serif;}",
    ".launcher{position:fixed;bottom:20px;right:20px;width:60px;height:60px;border-radius:50%;background:" + ACCENT + ";color:#fff;border:none;cursor:pointer;box-shadow:0 6px 20px rgba(0,0,0,.25);display:flex;align-items:center;justify-content:center;z-index:2147483000;transition:transform .15s ease;}",
    ".launcher:hover{transform:scale(1.06);}",
    ".launcher svg{width:28px;height:28px;}",
    ".panel{position:fixed;bottom:90px;right:20px;width:370px;max-width:calc(100vw - 32px);height:560px;max-height:calc(100vh - 120px);background:" + PAPER + ";border-radius:16px;box-shadow:0 12px 40px rgba(0,0,0,.28);display:none;flex-direction:column;overflow:hidden;z-index:2147483000;}",
    ".panel.open{display:flex;}",
    ".hdr{background:" + INK + ";color:#fff;padding:16px 18px;display:flex;align-items:center;justify-content:space-between;}",
    ".hdr h3{margin:0;font-size:15px;font-weight:600;}",
    ".hdr p{margin:2px 0 0;font-size:12px;opacity:.7;}",
    ".close{background:none;border:none;color:#fff;font-size:22px;line-height:1;cursor:pointer;opacity:.8;padding:0 4px;}",
    ".close:hover{opacity:1;}",
    ".body{flex:1;overflow-y:auto;padding:16px;display:flex;flex-direction:column;gap:10px;}",
    ".msg{max-width:82%;padding:10px 13px;border-radius:14px;font-size:14px;line-height:1.45;white-space:pre-wrap;word-wrap:break-word;overflow-wrap:anywhere;}",
    ".bot{align-self:flex-start;background:#fff;color:" + INK + ";border:1px solid #ece7df;border-bottom-left-radius:4px;}",
    ".user{align-self:flex-end;background:" + ACCENT + ";color:#fff;border-bottom-right-radius:4px;}",
    ".bot a{color:" + ACCENT + ";}",
    ".user a{color:#fff;}",
    ".typing{align-self:flex-start;display:flex;gap:4px;padding:12px 14px;}",
    ".typing span{width:7px;height:7px;border-radius:50%;background:#bbb;animation:pp-blink 1.2s infinite both;}",
    ".typing span:nth-child(2){animation-delay:.2s;}",
    ".typing span:nth-child(3){animation-delay:.4s;}",
    "@keyframes pp-blink{0%,80%,100%{opacity:.2;}40%{opacity:1;}}",
    ".foot{border-top:1px solid #ece7df;padding:10px;display:flex;gap:8px;align-items:flex-end;}",
    ".foot textarea{flex:1;resize:none;border:1px solid #ddd6cc;border-radius:10px;padding:9px 11px;font-size:14px;line-height:1.4;max-height:96px;outline:none;background:#fff;color:" + INK + ";}",
    ".foot textarea:focus{border-color:" + ACCENT + ";}",
    ".send{background:" + ACCENT + ";color:#fff;border:none;border-radius:10px;width:40px;height:40px;cursor:pointer;flex:0 0 auto;display:flex;align-items:center;justify-content:center;}",
    ".send:disabled{opacity:.5;cursor:default;}",
    ".send svg{width:18px;height:18px;}",
    ".branding{text-align:center;font-size:10px;color:#aaa;padding:0 0 6px;}"
  ].join("");
  root.appendChild(style);

  var bubbleIcon =
    '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z"/></svg>';
  var sendIcon =
    '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="22" y1="2" x2="11" y2="13"/><polygon points="22 2 15 22 11 13 2 9 22 2"/></svg>';

  var launcher = document.createElement("button");
  launcher.className = "launcher";
  launcher.setAttribute("aria-label", "Open chat with Alice");
  launcher.innerHTML = bubbleIcon;
  root.appendChild(launcher);

  var panel = document.createElement("div");
  panel.className = "panel";
  panel.setAttribute("role", "dialog");
  panel.setAttribute("aria-label", TITLE);
  panel.innerHTML =
    '<div class="hdr"><div><h3>' + esc(TITLE) + "</h3><p>Built by a painter, for painters</p></div>" +
    '<button class="close" aria-label="Close chat">×</button></div>' +
    '<div class="body"></div>' +
    '<div class="foot"><textarea rows="1" placeholder="Ask Alice…"></textarea>' +
    '<button class="send" aria-label="Send message">' + sendIcon + "</button></div>" +
    '<div class="branding">Powered by Paint Pals</div>';
  root.appendChild(panel);

  var bodyEl = panel.querySelector(".body");
  var input = panel.querySelector("textarea");
  var sendBtn = panel.querySelector(".send");
  var greeted = false;
  var busy = false;

  function addMsg(text, who) {
    var el = document.createElement("div");
    el.className = "msg " + (who === "user" ? "user" : "bot");
    el.innerHTML = who === "user" ? esc(text) : renderBot(text);
    bodyEl.appendChild(el);
    bodyEl.scrollTop = bodyEl.scrollHeight;
    return el;
  }

  function showTyping() {
    var t = document.createElement("div");
    t.className = "typing";
    t.innerHTML = "<span></span><span></span><span></span>";
    bodyEl.appendChild(t);
    bodyEl.scrollTop = bodyEl.scrollHeight;
    return t;
  }

  function openPanel() {
    panel.classList.add("open");
    if (!greeted) { addMsg(GREETING, "bot"); greeted = true; }
    setTimeout(function () { input.focus(); }, 50);
  }
  function closePanel() { panel.classList.remove("open"); }

  launcher.addEventListener("click", function () {
    panel.classList.contains("open") ? closePanel() : openPanel();
  });
  panel.querySelector(".close").addEventListener("click", closePanel);

  input.addEventListener("input", function () {
    input.style.height = "auto";
    input.style.height = Math.min(input.scrollHeight, 96) + "px";
  });
  input.addEventListener("keydown", function (e) {
    if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); }
  });
  sendBtn.addEventListener("click", send);

  function send() {
    var text = (input.value || "").trim();
    if (!text || busy) return;
    addMsg(text, "user");
    input.value = "";
    input.style.height = "auto";
    busy = true;
    sendBtn.disabled = true;
    var typing = showTyping();

    fetch(ENDPOINT, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ message: text, threadId: threadId })
    })
      .then(function (res) {
        if (res.status === 429) {
          throw { soft: "I'm getting a lot of questions right now — give me a moment and try again." };
        }
        if (!res.ok) throw new Error("HTTP " + res.status);
        return res.json();
      })
      .then(function (data) {
        typing.remove();
        if (data && data.threadId) {
          threadId = data.threadId;
          try { localStorage.setItem(THREAD_KEY, threadId); } catch (e) {}
        }
        addMsg((data && data.reply) || "Sorry, I didn't catch that — mind rephrasing?", "bot");
      })
      .catch(function (err) {
        typing.remove();
        addMsg(
          (err && err.soft) ||
            "Sorry — I hit a snag connecting. Please try again in a moment, or email the team and we'll help.",
          "bot"
        );
      })
      .then(function () {
        busy = false;
        sendBtn.disabled = false;
        input.focus();
      });
  }
})();
